import React from 'react';
import { ArrowUpRight } from 'lucide-react';

export type TileTone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

const TILE_TONE_CLASS: Record<TileTone, string> = {
  good: 'border-green-700/50 bg-green-900/10 hover:border-green-500/70 hover:bg-green-900/20',
  warn: 'border-amber-700/50 bg-amber-900/10 hover:border-amber-500/70 hover:bg-amber-900/20',
  bad: 'border-red-700/50 bg-red-900/10 hover:border-red-500/70 hover:bg-red-900/20',
  info: 'border-blue-700/40 bg-blue-900/10 hover:border-blue-500/60 hover:bg-blue-900/20',
  neutral: 'border-border bg-secondary/30 hover:border-yellow-600/60 hover:bg-secondary/50',
};

const TILE_ICON_CLASS: Record<TileTone, string> = {
  good: 'text-green-400',
  warn: 'text-amber-400',
  bad: 'text-red-400',
  info: 'text-blue-400',
  neutral: 'text-muted-foreground',
};

// A clickable summary tile for the client overview -- colour shows state at a
// glance (green ok / amber needs a look / red missing), the footer says where
// the click goes.
export const OverviewTile: React.FC<{
  icon: React.ElementType;
  title: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: TileTone;
  actionLabel: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ icon: Icon, title, value, hint, tone = 'neutral', actionLabel, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`group text-left rounded-xl border p-3.5 transition-colors flex flex-col gap-1.5 min-h-[112px] focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/60 disabled:opacity-60 disabled:cursor-wait ${TILE_TONE_CLASS[tone]}`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className={`w-4 h-4 shrink-0 ${TILE_ICON_CLASS[tone]}`} />
        {title}
      </span>
      <ArrowUpRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
    <div className="text-sm font-semibold text-foreground break-words line-clamp-2">{value}</div>
    {hint && <div className="text-xs text-muted-foreground break-words line-clamp-2">{hint}</div>}
    <div className="mt-auto pt-1 text-[10px] uppercase tracking-wide text-yellow-500/80 group-hover:text-yellow-400">
      {actionLabel}
    </div>
  </button>
);

// A titled block of tiles.
export const TileSection: React.FC<{
  icon: React.ElementType;
  title: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, hint, children }) => (
  <section className="space-y-2">
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </p>
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
  </section>
);
